import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, CheckCircle2, Play, Printer, SkipForward } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import { useAuth } from '@/context/AuthContext'
import type { DepartmentDeadline, Job } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import { Field, Input } from '@/components/ui/Field'
import { StateBadge } from '@/components/ui/Badge'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'
import IssuePhotoPicker, { type IssuePhoto } from '@/components/job/IssuePhotoPicker'
import JobDateStrip from '@/components/job/JobDateStrip'
import JobOrderItems from '@/components/job/JobOrderItems'

const ACTION_LABEL: Record<string, string> = {
  start: 'started',
  complete: 'completed',
  skip: 'skipped',
  issue: 'issue flagged',
}

/**
 * Mirrors the staff job page: the partner works the job's current section
 * with the same start / complete / issue buttons, plus "skip" because their
 * floor does not always have every section ours does. Skipping still moves
 * the job on, so the customer's timeline keeps reading true.
 */
export default function PartnerJobDetail() {
  const { token } = useParams()
  const { user } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [issueOpen, setIssueOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [photo, setPhoto] = useState<IssuePhoto | null>(null)

  const { data: payload, isLoading, error } = useQuery({
    queryKey: ['partner-job', token],
    queryFn: async () =>
      (await api.get<{ data: Job; department_deadlines?: DepartmentDeadline[] }>(`/partner/jobs/${token}`)).data,
    retry: false,
  })
  const job = payload?.data
  const deadlines = payload?.department_deadlines ?? []

  const stage = useMutation({
    mutationFn: async (payload: { action: string; reason?: string; photo_path?: string; photo_key?: string }) =>
      (await api.post(`/partner/jobs/${job!.id}/stage`, payload)).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['partner-job', token] })
      queryClient.invalidateQueries({ queryKey: ['partner-jobs'] })
      toast.success(data.message ?? 'Stage updated')
      closeIssue()
    },
    onError: (err) => toast.error('Update failed', errorMessage(err)),
  })

  const closeIssue = () => {
    setIssueOpen(false)
    setReason('')
    setPhoto(null)
  }

  if (isLoading) return <LogoLoader />

  if (error || !job) {
    return (
      <Card className="mx-auto max-w-md text-center">
        <p className="font-bold">Job not found</p>
        <p className="mt-1 text-sm text-muted">It may have been taken back by MPG Printing.</p>
        <Link to="/partner">
          <Button className="mt-4">Back to jobs</Button>
        </Link>
      </Card>
    )
  }

  const mine = job.warehouse?.id === user?.partner?.id
  const section = job.current_department
  const started = job.stage_logs?.some((log) => log.department === section?.name && log.action === 'start')
  const order = job.order

  // In the header from sm, on the Order details card below it.
  const sheetButton = (
    <Link to={`/partner/jobs/${job.id}/sheet`} target="_blank" rel="noreferrer">
      <Button variant="outline" className="max-sm:h-7 max-sm:gap-1.5 max-sm:px-2.5 max-sm:text-xs">
        <Printer className="size-4 max-sm:size-3.5" /> Sheet
      </Button>
    </Link>
  )

  return (
    <div>
      <Link to="/partner" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline">
        <ArrowLeft className="size-4" /> Back
      </Link>
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 sm:gap-2">
            <span className="font-mono text-base sm:text-lg">{job.display_code ?? job.code}</span>
            {/* Phones: the status rides beside the code instead of on its own row. */}
            <span className="flex sm:hidden">
              <StateBadge state={job.status} />
            </span>
          </span>
        }
        subtitle={`Order ${order?.number} - ${order?.total_pieces ?? ''} pcs`}
        hideActionsOnMobile
        actions={
          <>
            <StateBadge state={job.status} />
            {sheetButton}
          </>
        }
      />

      <JobDateStrip job={job} deadlines={deadlines} />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* The compact 28px phone button overhangs the heading line by 2px each side rather than making the row taller. */}
        <JobOrderItems order={order} action={<div className="-my-0.5 sm:hidden">{sheetButton}</div>} />

        <Card>
          <SectionLabel>
            Update stage - {section?.name ?? (job.status === 'cancelled' ? 'cancelled' : 'completed')}
          </SectionLabel>
          {job.status === 'cancelled' ? (
            <p className="rounded-lg border border-danger/60 bg-danger/20 p-3 text-sm font-semibold text-danger">
              This job was cancelled. Stop work on it and return the job sheet to the office.
            </p>
          ) : !mine ? (
            <p className="text-sm text-muted">This job is no longer assigned to your company.</p>
          ) : !section ? (
            <p className="text-sm text-muted">Production is complete. Nothing left to update.</p>
          ) : (
            <div className="grid gap-2">
              <Button
                disabled={started || stage.isPending}
                loading={stage.isPending && stage.variables?.action === 'start'}
                onClick={() => stage.mutate({ action: 'start' })}
              >
                <Play className="size-4" /> Start work
              </Button>
              <Button
                variant="outline"
                disabled={!started || stage.isPending}
                loading={stage.isPending && stage.variables?.action === 'complete'}
                onClick={() => stage.mutate({ action: 'complete' })}
                className="border-success/60 bg-success/20 text-success hover:bg-success/5"
              >
                <CheckCircle2 className="size-4" /> Complete - pass to next section
              </Button>
              <Button
                variant="outline"
                disabled={stage.isPending}
                loading={stage.isPending && stage.variables?.action === 'skip'}
                onClick={() => stage.mutate({ action: 'skip' })}
              >
                <SkipForward className="size-4" /> Skip this section
              </Button>
              <Button variant="danger" disabled={stage.isPending} onClick={() => setIssueOpen(true)}>
                <AlertTriangle className="size-4" /> Flag issue
              </Button>
              <p className="text-xs text-muted">
                Skip a section your floor does not do. The job moves on and the customer sees it as passed.
              </p>
            </div>
          )}

          <SectionLabel className="mt-6">History</SectionLabel>
          <ol className="space-y-2 text-xs">
            {job.stage_logs?.map((log) => (
              <li key={log.id} className="flex items-center justify-between rounded-lg bg-app px-3 py-2">
                <span className="font-semibold">
                  {log.department} - {ACTION_LABEL[log.action] ?? log.action}
                  {log.reason && <span className="block font-normal text-status-danger">{log.reason}</span>}
                  {log.photo_path && (
                    <a href={log.photo_path} target="_blank" rel="noreferrer" className="mt-1 block w-fit">
                      <img
                        src={log.photo_path}
                        alt="Photo of the problem"
                        className="size-16 rounded-lg border border-foreground/15 object-cover"
                      />
                    </a>
                  )}
                </span>
                <span className="text-muted">{formatDateTime(log.at)}</span>
              </li>
            ))}
            {(job.stage_logs?.length ?? 0) === 0 && <p className="text-muted">No updates yet.</p>}
          </ol>
        </Card>
      </div>

      <Modal
        open={issueOpen}
        onClose={closeIssue}
        title="Flag production issue"
        footer={
          <>
            <Button variant="outline" onClick={closeIssue}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={stage.isPending}
              disabled={!reason.trim()}
              onClick={() => stage.mutate({ action: 'issue', reason, ...(photo ?? {}) })}
            >
              Flag issue
            </Button>
          </>
        }
      >
        <Field label="What is wrong?">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. fabric out of stock" />
        </Field>
        <Field label="Photo of the problem (optional)">
          <IssuePhotoPicker endpoint="/partner/issue-photo" value={photo} onChange={setPhoto} disabled={stage.isPending} />
        </Field>
        <p className="mt-2 text-xs text-muted">
          MPG Printing's admins are notified and the order is marked as issue until it is resolved.
        </p>
      </Modal>
    </div>
  )
}
